"use client";

import * as React from "react";

import { cx } from "../../lib/cx";
import { useClickAfuera, useFlotante } from "../../lib/flotante";
import { Icon, type IconName } from "../core/Icon";
import { Field, idEtiqueta, idMensaje } from "./Field";
import c from "./Control.module.css";
import s from "./Select.module.css";

export interface SelectOption {
  value: string;
  label: string;
  disabled?: boolean;
  /** Una segunda línea chica y gris debajo del `label`, sólo en la lista ("Ya tiene un cartel instalado"). */
  description?: string;
  /** Un dato chico en mono a la derecha del `label`, sólo en la lista: cuántas consultas tiene esa propiedad. */
  detail?: string;
}

/** Lo que recibe `onChange`. Alcanza con `e.target.value`, como con un `<select>`. */
export interface SelectChangeEvent {
  target: { value: string; name: string | undefined };
}

export interface SelectProps
  extends Omit<React.ComponentPropsWithoutRef<"button">, "onChange" | "value" | "defaultValue" | "type"> {
  label?: React.ReactNode;
  hint?: React.ReactNode;
  /** Qué pasó y qué hacer. Pinta el borde de rojo y se anuncia como alerta. */
  error?: React.ReactNode;
  options: SelectOption[];
  /** Controlado. Con `onChange`. */
  value?: string;
  /** No controlado. Sin él, arranca en la primera opción habilitada (como un `<select>`), salvo que haya `placeholder`. */
  defaultValue?: string;
  onChange?: (e: SelectChangeEvent) => void;
  /** Texto apagado cuando no hay valor. Con placeholder el valor inicial es "". */
  placeholder?: string;
  /** Icono a la izquierda, como en `Input`. */
  icon?: IconName;
  /** Para el formulario: un `<input type="hidden">` con este nombre lleva el valor. Deshabilitado no se manda. */
  name?: string;
  form?: string;
  /** Marca visual en la etiqueta y `aria-required`. La validación la hace quien recibe el formulario. */
  required?: boolean;
  /** field = un campo de formulario (default). filter = la píldora de un filtro (la barra de un listado): hugea su
   *  contenido, muestra el `placeholder` mientras el valor es "" (la opción vacía es "sin filtrar") y se pinta de
   *  accent cuando hay algo elegido o está abierta; el menú se pega a su borde derecho. */
  variant?: "field" | "filter";
  /** Sólo con `variant="filter"`: ancho mínimo del menú, en px (default 180), para opciones largas (direcciones). */
  menuMinWidth?: number;
}

const ALTO_LISTA = 288;
const ANCHO_MENU_FILTRO = 180;
const VENTANA_BUSQUEDA_MS = 500;
const SALTO_PAGINA = 10;

function tecla(e: React.KeyboardEvent): string | null {
  if (e.key.length !== 1 || e.altKey || e.ctrlKey || e.metaKey) return null;
  return e.key.toLocaleLowerCase();
}

/**
 * Lista desplegable propia: un botón `combobox` que abre un `listbox` dibujado
 * por el design system, no por el sistema operativo. Misma API de uso que el
 * `<select>` que reemplaza (`options`, `name`, `defaultValue`, `value`,
 * `onChange` con `e.target.value`), así que un formulario no se entera.
 *
 * Teclado como el nativo: flechas, Enter y espacio abren; con la lista abierta,
 * flechas, Home, End, RePág y AvPág mueven, escribir busca por prefijo, Enter
 * elige, Escape cierra y devuelve el foco. Cerrado, escribir cambia el valor
 * directo. El foco vive en el listbox mientras está abierto
 * (`aria-activedescendant`); la lista se posiciona en fijo (ver `useFlotante`).
 */
export function Select({
  label,
  hint,
  error,
  options,
  value,
  defaultValue,
  onChange,
  placeholder,
  icon,
  name,
  form,
  id,
  required,
  disabled,
  variant = "field",
  menuMinWidth = ANCHO_MENU_FILTRO,
  className,
  style,
  onClick,
  onKeyDown,
  ...rest
}: SelectProps) {
  const esFiltro = variant === "filter";
  const generado = React.useId();
  const uid = id ?? generado;
  const idLista = `${uid}-lista`;
  const idValor = `${uid}-valor`;

  const primeraHabilitada = options.find((o) => !o.disabled)?.value ?? "";
  const inicial = defaultValue ?? (placeholder !== undefined ? "" : primeraHabilitada);
  const [interno, setInterno] = React.useState(inicial);
  const controlado = value !== undefined;
  const actual = controlado ? value : interno;
  const elegida = options.find((o) => o.value === actual);

  const [abierto, setAbierto] = React.useState(false);
  const [activo, setActivo] = React.useState(0);

  const raiz = React.useRef<HTMLSpanElement>(null);
  const caja = React.useRef<HTMLSpanElement>(null);
  const boton = React.useRef<HTMLButtonElement>(null);
  const lista = React.useRef<HTMLUListElement>(null);
  const busqueda = React.useRef({ texto: "", hora: 0 });

  const flotante = useFlotante(caja, abierto, esFiltro ? { altoMax: ALTO_LISTA, anchoMin: menuMinWidth, alinear: "derecha" } : { altoMax: ALTO_LISTA });
  const cerrarSinFoco = React.useCallback(() => setAbierto(false), []);
  useClickAfuera(raiz, abierto, cerrarSinFoco);

  function elegir(v: string) {
    if (!controlado) setInterno(v);
    if (v !== actual) onChange?.({ target: { value: v, name } });
  }

  /** El siguiente índice habilitado desde `desde` en la dirección dada, sin dar la vuelta. */
  function habilitado(desde: number, direccion: 1 | -1): number {
    for (let i = desde; i >= 0 && i < options.length; i += direccion) {
      if (!options[i]!.disabled) return i;
    }
    return -1;
  }

  function abrir() {
    if (disabled) return;
    busqueda.current = { texto: "", hora: 0 };
    const i = options.findIndex((o) => o.value === actual && !o.disabled);
    setActivo(i >= 0 ? i : habilitado(0, 1));
    setAbierto(true);
  }

  function cerrar() {
    setAbierto(false);
    busqueda.current = { texto: "", hora: 0 };
    boton.current?.focus();
  }

  /** Búsqueda por prefijo, como el `<select>`: la misma letra repetida recorre las que empiezan con ella. */
  function buscar(letra: string, desde: number): number {
    const ahora = Date.now();
    const b = busqueda.current;
    b.texto = ahora - b.hora < VENTANA_BUSQUEDA_MS ? b.texto + letra : letra;
    b.hora = ahora;
    // "aaa" recorre las que empiezan con "a"; "ab" busca el prefijo entero.
    const todasIguales = b.texto.split("").every((ch) => ch === letra);
    const prefijo = todasIguales ? letra : b.texto;
    const arranque = todasIguales ? desde + 1 : desde;
    for (let k = 0; k < options.length; k++) {
      const i = (arranque + k) % options.length;
      const o = options[i]!;
      if (!o.disabled && o.label.toLocaleLowerCase().startsWith(prefijo)) return i;
    }
    return -1;
  }

  React.useEffect(() => {
    if (abierto) lista.current?.focus({ preventScroll: true });
  }, [abierto]);

  React.useEffect(() => {
    if (!abierto) return;
    lista.current?.querySelector<HTMLElement>("[data-activo]")?.scrollIntoView({ block: "nearest" });
  }, [abierto, activo]);

  // Un `form.reset()` no toca el estado de React: se escucha para volver al inicial.
  React.useEffect(() => {
    const f = boton.current?.form;
    if (!f || controlado) return;
    const volver = () => setInterno(inicial);
    f.addEventListener("reset", volver);
    return () => f.removeEventListener("reset", volver);
  }, [controlado, inicial]);

  function onClickBoton(e: React.MouseEvent<HTMLButtonElement>) {
    onClick?.(e);
    if (e.defaultPrevented) return;
    // `detail === 0` es el click que manda la etiqueta (o el teclado, que ya
    // se atendió en keydown): como en el nativo, enfoca y no abre.
    if (e.detail === 0) {
      boton.current?.focus();
      return;
    }
    if (abierto) cerrar();
    else abrir();
  }

  function onKeyBoton(e: React.KeyboardEvent<HTMLButtonElement>) {
    onKeyDown?.(e);
    if (e.defaultPrevented || disabled) return;
    if (e.key === "ArrowDown" || e.key === "ArrowUp" || e.key === "Enter" || e.key === " ") {
      e.preventDefault();
      abrir();
      return;
    }
    const letra = tecla(e);
    if (letra) {
      const i = buscar(letra, options.findIndex((o) => o.value === actual));
      if (i >= 0) elegir(options[i]!.value);
    }
  }

  function onKeyLista(e: React.KeyboardEvent<HTMLUListElement>) {
    let destino = -1;
    switch (e.key) {
      case "ArrowDown":
        destino = habilitado(activo + 1, 1);
        break;
      case "ArrowUp":
        destino = habilitado(activo - 1, -1);
        break;
      case "Home":
        destino = habilitado(0, 1);
        break;
      case "End":
        destino = habilitado(options.length - 1, -1);
        break;
      case "PageDown":
        destino = habilitado(Math.min(options.length - 1, activo + SALTO_PAGINA), -1);
        break;
      case "PageUp":
        destino = habilitado(Math.max(0, activo - SALTO_PAGINA), 1);
        break;
      case " ":
        // Con una búsqueda en curso, el espacio es parte del prefijo ("casa quinta").
        if (Date.now() - busqueda.current.hora < VENTANA_BUSQUEDA_MS) {
          e.preventDefault();
          destino = buscar(" ", activo);
          break;
        }
      // falls through
      case "Enter": {
        e.preventDefault();
        const o = options[activo];
        if (o && !o.disabled) elegir(o.value);
        cerrar();
        return;
      }
      case "Escape":
        e.preventDefault();
        cerrar();
        return;
      case "Tab":
        // Se deja ir el foco; la lista se cierra sola por el blur de la raíz.
        setAbierto(false);
        return;
      default: {
        const letra = tecla(e);
        if (letra) destino = buscar(letra, activo);
      }
    }
    if (destino >= 0) {
      e.preventDefault();
      setActivo(destino);
    }
  }

  function onBlurRaiz(e: React.FocusEvent<HTMLSpanElement>) {
    if (!raiz.current?.contains(e.relatedTarget as Node | null)) setAbierto(false);
  }

  return (
    <Field htmlFor={uid} label={label} hint={hint} error={error} required={required} className={className} style={style}>
      <span ref={raiz} className={s.raiz} onBlur={onBlurRaiz}>
        <span
          ref={caja}
          className={c.caja}
          data-select=""
          data-filtro={esFiltro ? "" : undefined}
          data-activo={esFiltro && actual !== "" ? "" : undefined}
          data-error={error ? "" : undefined}
          data-abierto={abierto ? "" : undefined}
        >
          {icon ? (
            <span className={c.icono}>
              <Icon name={icon} size={16} />
            </span>
          ) : null}
          <button
            ref={boton}
            id={uid}
            type="button"
            role="combobox"
            aria-haspopup="listbox"
            aria-expanded={abierto}
            aria-controls={abierto ? idLista : undefined}
            aria-labelledby={label ? `${idEtiqueta(uid)} ${idValor}` : undefined}
            aria-required={required || undefined}
            aria-invalid={error ? true : undefined}
            aria-describedby={error || hint ? idMensaje(uid) : undefined}
            disabled={disabled}
            className={cx(c.control, s.boton)}
            onClick={onClickBoton}
            onKeyDown={onKeyBoton}
            {...rest}
          >
            <span id={idValor} className={cx(s.valor, !esFiltro && !elegida && s.placeholder)}>
              {esFiltro && actual === "" ? (placeholder ?? "\u{a0}") : (elegida?.label ?? placeholder ?? "\u{a0}")}
            </span>
          </button>
          <span className={c.flecha}>
            <Icon name="chevron-down" size={16} />
          </span>
        </span>

        {name && !disabled ? <input type="hidden" name={name} value={actual} form={form} /> : null}

        {abierto ? (
          <ul
            ref={lista}
            id={idLista}
            role="listbox"
            tabIndex={-1}
            aria-labelledby={label ? idEtiqueta(uid) : undefined}
            aria-activedescendant={options[activo] ? `${idLista}-${activo}` : undefined}
            className={s.lista}
            data-arriba={flotante.arriba ? "" : undefined}
            style={flotante.style}
            onKeyDown={onKeyLista}
          >
            {options.map((o, i) => (
              <li
                key={o.value}
                id={`${idLista}-${i}`}
                role="option"
                aria-selected={o.value === actual}
                aria-disabled={o.disabled || undefined}
                data-activo={i === activo ? "" : undefined}
                className={s.opcion}
                onPointerMove={() => {
                  if (!o.disabled && i !== activo) setActivo(i);
                }}
                onClick={() => {
                  if (o.disabled) return;
                  elegir(o.value);
                  cerrar();
                }}
              >
                <span className={s.opcionTexto}>
                  {o.label}
                  {o.description ? <span className={s.opcionDescripcion}>{o.description}</span> : null}
                </span>
                {o.detail ? <span className={s.opcionDetalle}>{o.detail}</span> : null}
                {o.value === actual ? (
                  <span className={s.tilde}>
                    <Icon name="check" size={16} />
                  </span>
                ) : null}
              </li>
            ))}
          </ul>
        ) : null}
      </span>
    </Field>
  );
}
